import { Refresh, TrendingUp } from '@mui/icons-material';
import {
    Avatar,
    AvatarGroup,
    Box,
    Button,
    CircularProgress,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Popover,
    Typography
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePointsContext } from '../context/PointsContext';

const UserPointsTotal: React.FC = () => {
    const { pointsTotal, loading, error, refreshPoints } = usePointsContext();
    const { enqueueSnackbar } = useSnackbar();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const previousPointsRef = useRef<number | null>(null);
    const didUserTriggerRef = useRef(false);

    const open = Boolean(anchorEl);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleRefresh = () => {
        previousPointsRef.current = pointsTotal;
        didUserTriggerRef.current = true;
        refreshPoints();
        handleClose();
    };

    useEffect(() => {
        if (!loading && didUserTriggerRef.current) {
            didUserTriggerRef.current = false;
            const previous = previousPointsRef.current;
            if (pointsTotal == null) return;
            const changed = previous !== null && previous !== pointsTotal;
            if (changed) {
                enqueueSnackbar(`Points updated! You now have ${pointsTotal.toLocaleString()} pts`, {
                    variant: 'success',
                    autoHideDuration: 4000
                });
            } else {
                enqueueSnackbar('Points refreshed', {
                    variant: 'info',
                    autoHideDuration: 2500
                });
            }
        }
    }, [loading, pointsTotal, enqueueSnackbar]);

    if (pointsTotal == null) return null;

    return (
        <>
            <Button
                variant="outlined"
                color="primary"
                size="small"
                aria-haspopup="true"
                aria-expanded={open}
                onClick={handleOpen}
                disabled={loading}
                data-attr="user-points-total_open"
                endIcon={<AvatarGroup
                    sx={{
                        '& .MuiAvatar-root': {
                            width: 18,
                            height: 18
                        }
                    }}
                >
                    <Avatar>
                        <TrendingUp fontSize="small" sx={{ color: 'primary.dark' }} />
                    </Avatar>
                    <Avatar>
                        <Refresh fontSize="small" sx={{ color: 'primary.light' }} />
                    </Avatar>
                </AvatarGroup>}
                sx={{
                    textTransform: 'none'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1">
                        {pointsTotal.toLocaleString()}
                    </Typography>
                    {error ? (
                        <Typography variant="caption" color="error" component="span" sx={{ fontSize: '0.65rem' }}>
                            {error}
                        </Typography>
                    ) : (
                        <Typography
                            component="span"
                            variant="body2"
                            sx={{
                                fontWeight: 700,
                                fontSize: '0.7rem',
                            }}
                        >
                            points earned
                        </Typography>
                    )}
                </Box>
            </Button>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <List dense sx={{ minWidth: 180, py: 0 }}>
                    <ListItemButton onClick={handleRefresh} disabled={loading} data-attr="user-points-total_refresh">
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            {loading ? (
                                <CircularProgress size={18} color="inherit" />
                            ) : (
                                <Refresh fontSize="small" />
                            )}
                        </ListItemIcon>
                        <ListItemText primary={loading ? 'Refreshing…' : 'Refresh points'} />
                    </ListItemButton>
                    <ListItemButton component={Link} to="/my-profile/points" onClick={handleClose} data-attr="user-points-total_view-all">
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <TrendingUp fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="View all points" />
                    </ListItemButton>
                </List>
            </Popover>
        </>
    );
};

export default UserPointsTotal;
